import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "../ui/alert-dialog";

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm text-gray-800 text-right font-medium" data-testid={testId}>{value}</span>
  </div>
);

/** Final summary shown before an OFFER_EXTENDED/HIRED applicant is converted into an internal
 * DRAFT Employee Application (never an employee master record or STAFF account directly -
 * see recruitment_service.convert_to_application). Duplicate conversion is blocked server-side
 * (converted_application_id already set) - this dialog is never reachable in that state since
 * the "Convert" button itself is hidden once converted (see ApplicantActionPanel). */
export const ConvertConfirmDialog = ({ open, onOpenChange, applicant, job, interviewOutcomeLabel, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } catch (e) {
      // Error is already surfaced via toast by the page-level action helper - keep the
      // dialog open so the recruiter can see what happened and retry.
    } finally {
      setSubmitting(false);
    }
  };

  const warnings = [];
  if (applicant?.work_authorization_review_required) warnings.push("Work authorization has not been verified yet - it stays review-required after conversion.");
  if (applicant?.availability_review_required) warnings.push("Applicant's weekly availability is below this position's stated minimum.");

  return (
    <AlertDialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <AlertDialogContent data-testid="convert-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Convert to Employee Application</AlertDialogTitle>
          <AlertDialogDescription>
            This creates a DRAFT internal Employee Application from this applicant's record. It does not create
            an employee master record, a STAFF login, or any invitation - onboarding staff will pick it up from
            the Employee Applications queue.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-1">
          <Row label="Applicant" value={applicant?.legal_name} testId="convert-summary-applicant" />
          <Row label="Job" value={job?.title_en || "-"} testId="convert-summary-job" />
          <Row label="Store" value={job?.store_location || "Organization-wide"} testId="convert-summary-store" />
          <Row label="Interview outcome" value={interviewOutcomeLabel} testId="convert-summary-interview" />
          <Row label="Work authorization" value={applicant?.work_authorization_review_required ? "Review required" : "No review flagged"} testId="convert-summary-workauth" />
          <Row label="Availability" value={applicant?.availability_review_required ? "Review required" : "Meets stated minimum"} testId="convert-summary-availability" />
        </div>

        {warnings.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="convert-summary-warnings">
            {warnings.map((w, i) => (
              <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {w}</p>
            ))}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="convert-dialog-cancel" disabled={submitting}>Back</AlertDialogCancel>
          <button
            data-testid="convert-dialog-confirm"
            disabled={submitting}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Converting..." : "Confirm Conversion"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
